import { JobSubtask, JobTaskStatus } from '@root/globalTypes';
import { JobSubTaskInput } from '../input';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@components/ui/button';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { useConcreteUpdateForm } from '@hooks/forms/useConcreteUpdateForm';
import { FormField } from '@components/ui/form';
import { useJobContext } from '@contexts/job';
import { useAppContext } from '@contexts/app';
import { parseDecimalValue } from '@utils/formHelpers';

interface JobSubtaskConcreteUpdateProps {
  data: JobSubtask;
  status?: JobTaskStatus;
}

export const JobSubtaskConcreteUpdate = ({ data, status }: JobSubtaskConcreteUpdateProps) => {
  const { t } = useTranslation('app');
  const formRef = useRef<HTMLFormElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { archived } = useJobContext();
  const { isCadTechnician } = useAppContext();

  const material = data?.materials?.[0];

  useOutsideClick(formRef, () => setIsVisible(false));

  const {
    form,
    fieldArray: { fields, append, remove },
    onSubmit,
  } = useConcreteUpdateForm({
    material,
    setIsVisible,
    successHandler: () => {
      form.reset();
    },
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (!material) return;
    reset();
    remove();
    const { options } = material;
    options?.map((option) => append(option));
  }, [material, append, remove, reset]);

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      onFocus={() => setIsVisible(true)}
      ref={formRef}
      className="grid grid-cols-[repeat(4,minmax(min-content,max-content))] items-center gap-x-4 gap-y-1 px-4 py-2.5">
      {fields.map((option, index) => (
        <React.Fragment key={option.id}>
          {option.includeEstimated && (
            <JobSubTaskInput
              disabled
              value={[
                option.valueType === 'number'
                  ? parseDecimalValue(option.estimatedQuantity || '')
                  : option.estimatedQuantity || '-',
                option.unit,
              ].join(' ')}
              label={t('pages.job.info.inputs.estimated', { name: option.name })}
              placeholder={option?.estimatedQuantity || '0'}
              suffix={option?.unit || ''}
            />
          )}
          <FormField
            control={form.control}
            name={`optionsAttributes.${index}.finalQuantity`}
            render={({ field }) => (
              <JobSubTaskInput
                {...field}
                id={`final-quantity-${option?.id}`}
                disabled={status === 'completed' || data.completed || archived || isCadTechnician}
                placeholder={option?.finalQuantity || '0'}
                label={t('pages.job.info.inputs.final', { name: option.name })}
                suffix={option?.unit || ''}
              />
            )}
          />
        </React.Fragment>
      ))}
      {isVisible && (
        <Button
          disabled={status === 'completed' || data.completed || archived || isCadTechnician}
          type="submit"
          className="col-start-4">
          {t('actions.save')}
        </Button>
      )}
    </form>
  );
};
